import { useMemo } from "react";

import {
  diffPromptWords,
  type PromptDiffSegment,
} from "@/features/script-review/lib/prompt-diff";

const ADDED_CLASS =
  "rounded-sm bg-[color-mix(in_srgb,var(--success)_18%,transparent)] font-medium text-success-subtle-foreground underline decoration-2 decoration-success underline-offset-2 box-decoration-clone";
const REMOVED_CLASS =
  "rounded-sm bg-error-subtle font-medium text-error-subtle-foreground line-through box-decoration-clone";
const REPLACEMENT_CLASS = "inline-flex flex-wrap items-baseline gap-x-1";

type DiffRun =
  | { kind: "same" | "added" | "removed"; text: string }
  | { kind: "replaced"; removed: string; added: string };

/** Pair a deletion with the following insertion so a swap reads as one change. */
export function groupDiffSegments(segments: readonly PromptDiffSegment[]): DiffRun[] {
  const runs: DiffRun[] = [];
  for (let i = 0; i < segments.length; i++) {
    const current = segments[i]!;
    const next = segments[i + 1];
    if (current.op === "removed" && next?.op === "added") {
      runs.push({ kind: "replaced", removed: current.text, added: next.text });
      i += 1;
      continue;
    }
    if (current.op === "added" && next?.op === "removed") {
      runs.push({ kind: "replaced", removed: next.text, added: current.text });
      i += 1;
      continue;
    }
    runs.push({ kind: current.op, text: current.text });
  }
  return runs;
}

export interface PersonaScriptFieldDiffProps {
  /** Previous version of this cell; omit to render `after` without marks. */
  before?: string;
  after: string;
  empty: string;
}

function AddedText({ text }: { text: string }) {
  return <mark className={ADDED_CLASS}>{text}</mark>;
}

function RemovedText({ text }: { text: string }) {
  return <del className={REMOVED_CLASS}>{text}</del>;
}

/** Word-level unified diff so a small edit in a long scene cell is findable. */
export function PersonaScriptFieldDiff({
  before,
  after,
  empty,
}: PersonaScriptFieldDiffProps) {
  const segments = useMemo(
    () => (before == null ? null : diffPromptWords(before, after)),
    [after, before],
  );

  if (segments == null) {
    return <>{after || empty}</>;
  }
  if (!after && !before) {
    return <>{empty}</>;
  }
  if (segments.every((segment) => segment.op === "same")) {
    return <>{after || empty}</>;
  }

  return (
    <>
      {groupDiffSegments(segments).map((run, index) => {
        switch (run.kind) {
          case "same":
            return run.text ? <span key={index}>{run.text}</span> : null;
          case "added":
            return run.text ? <AddedText key={index} text={run.text} /> : null;
          case "removed":
            return run.text ? <RemovedText key={index} text={run.text} /> : null;
          case "replaced":
            return (
              <span key={index} className={REPLACEMENT_CLASS}>
                {run.removed ? <RemovedText text={run.removed} /> : null}
                {run.added ? <AddedText text={run.added} /> : null}
              </span>
            );
        }
      })}
    </>
  );
}
